import React from 'react';
import { LoggerService } from '@honeybook/core-web';
/**
 * Recovers from a Module Federation stale shared-singleton version skew by reloading
 * once to fetch the current bundle set. ANY other error is rethrown so the app's route
 * ErrorBoundary handles it exactly as before — this boundary is narrow on purpose.
 *
 * The skew happens when a stale/cached bundle seeds an older copy of a shared singleton
 * into the share scope during a rolling deploy; a consuming MFE that needs a newer
 * version then throws at lazy render (`loadShare` rejects under `strictVersion`),
 * white-screening the whole app.
 */
export class StaleSharedSingletonBoundary extends React.Component {
    constructor() {
        super(...arguments);
        this.logger = new LoggerService({
            messagePrefix: '[MfeLoader]'
        });
        this.state = { error: null };
    }
    // MF's strictVersion failure message, verified identical across enhanced 0.22.0
    // and 2.6.0 (runtime-core `utils/share`):
    // "[ Federation Runtime ]: Version X from Y of shared singleton module Z does not
    // satisfy the requirement of W which needs <range>".
    // We require BOTH the MF runtime prefix AND the version-mismatch phrase so an
    // unrelated error that merely contains the phrase can't trip the reload. If MF ever
    // rewords either token this narrows to no-match → we fall through to rethrow (today's
    // behavior), never worse.
    static { this.SKEW_MESSAGE_PATTERN = /\[ Federation Runtime \][\s\S]*does not satisfy the requirement/; }
    // One-shot reload guard, tab-scoped and self-healing after the window. Stored in
    // sessionStorage so it survives the reload it triggers and a real failure can't loop.
    static { this.LAST_RELOAD_KEY = 'hb:mf-stale-reload'; }
    static { this.RELOAD_GUARD_WINDOW_MS = 15_000; }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error) {
        if (this.canRecover(error)) {
            this.logger.error('stale shared-singleton version skew detected — reloading once to recover', { mf_error_message: error.message });
            this.markReloaded();
            window.location.reload();
        }
    }
    render() {
        const { error } = this.state;
        if (error) {
            // Recoverable, first time → render nothing while componentDidCatch reloads.
            // Otherwise (not our error, or a reload already happened this window) →
            // rethrow to the route ErrorBoundary instead of looping.
            if (this.canRecover(error)) {
                return null;
            }
            throw error;
        }
        return this.props.children;
    }
    // Recoverable = our stale-singleton skew AND we haven't already spent this
    // session's one reload attempt on it.
    canRecover(error) {
        return (this.isStaleSharedSingletonError(error) &&
            !this.reloadedRecently() &&
            !this.isReloadedNavigation());
    }
    // Second, storage-independent stop. The sessionStorage guard defends against
    // storage being *unavailable*, but not against a write that succeeds and then
    // doesn't survive the reload it guards — which some bot renderers and privacy
    // modes do, and bots were ~88% of the error population in the skew incident.
    // In that case the key is missing on every fresh load, so a persistent skew
    // would reload forever. The navigation type comes from the browser, so it
    // survives losing storage: if this load is itself a reload and we're still
    // skewed, reloading again won't help — surface the error instead.
    isReloadedNavigation() {
        try {
            const [entry] = window.performance?.getEntriesByType?.('navigation') ?? [];
            return entry?.type === 'reload';
        }
        catch {
            // Unsupported or blocked — fall back to the sessionStorage guard alone.
            return false;
        }
    }
    isStaleSharedSingletonError(error) {
        return (error instanceof Error &&
            StaleSharedSingletonBoundary.SKEW_MESSAGE_PATTERN.test(error.message));
    }
    reloadedRecently() {
        try {
            const last = Number(window.sessionStorage.getItem(StaleSharedSingletonBoundary.LAST_RELOAD_KEY));
            return (Number.isFinite(last) &&
                Date.now() - last <
                    StaleSharedSingletonBoundary.RELOAD_GUARD_WINDOW_MS);
        }
        catch {
            // If storage is unavailable we can't guard against a loop — assume we
            // already reloaded so we surface the error instead of risking a loop.
            return true;
        }
    }
    markReloaded() {
        try {
            window.sessionStorage.setItem(StaleSharedSingletonBoundary.LAST_RELOAD_KEY, String(Date.now()));
        }
        catch {
            // best-effort; reloadedRecently() returns true on the storage-error path
        }
    }
}
export default StaleSharedSingletonBoundary;
//# sourceMappingURL=StaleSharedSingletonBoundary.js.map